// Feature 5 — AI Tutor Chat
// Slide-in panel that lets the learner ask follow-up questions about their lesson.
// Uses streaming SSE from /api/tutor-chat.

import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';

// Build a plain-text summary of the lesson to pass as context
function buildLessonContext(lesson) {
  if (!lesson) return '';
  const parts = [];
  if (lesson.title)   parts.push(`Title: ${lesson.title}`);
  if (lesson.summary) parts.push(`Summary: ${lesson.summary}`);
  if (lesson.keyPoints?.length) {
    parts.push('Key points: ' + lesson.keyPoints.join('; '));
  }
  if (lesson.content?.length) {
    lesson.content.forEach(s => {
      if (s.heading && s.body) parts.push(`${s.heading}: ${s.body}`);
    });
  }
  if (lesson.practicalExample) parts.push(`Example: ${lesson.practicalExample}`);
  if (lesson.takeaways?.length) parts.push('Takeaways: ' + lesson.takeaways.join('; '));
  return parts.join('\n');
}

export default function TutorChat({ lesson, subject, level, onClose }) {
  const { background, learningNotes, userAge } = useAuth();

  const lessonContent = buildLessonContext(lesson);

  const SEED_MSG = {
    role:    'assistant',
    content: `Hi! I just helped you learn about **${subject}**. What would you like to explore further? You can ask me to clarify anything from the lesson, go deeper on a concept, or give you more examples.`,
  };

  const [messages,   setMessages]   = useState([SEED_MSG]);
  const [input,      setInput]      = useState('');
  const [streaming,  setStreaming]  = useState(false);
  const [error,      setError]      = useState('');
  const bottomRef  = useRef(null);
  const inputRef   = useRef(null);
  const abortRef   = useRef(null);

  // Auto-scroll to bottom
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Focus input on open
  useEffect(() => {
    setTimeout(() => inputRef.current?.focus(), 100);
  }, []);

  // Close on Escape
  useEffect(() => {
    const h = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [onClose]);

  const sendMessage = useCallback(async () => {
    const text = input.trim();
    if (!text || streaming) return;

    setInput('');
    setError('');

    // Add user message
    const userMsg = { role: 'user', content: text };
    const updatedMessages = [...messages, userMsg];
    setMessages(updatedMessages);

    // Add empty AI message placeholder
    const aiPlaceholder = { role: 'assistant', content: '' };
    setMessages(prev => [...prev, aiPlaceholder]);
    setStreaming(true);

    // Build messages array for API (exclude seed message from history)
    const apiMessages = updatedMessages
      .filter(m => m.role === 'user' || (m.role === 'assistant' && m !== SEED_MSG))
      .map(m => ({ role: m.role, content: m.content }));

    try {
      const controller = new AbortController();
      abortRef.current = controller;

      const res = await fetch('/api/tutor-chat', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        signal:  controller.signal,
        body: JSON.stringify({
          messages: apiMessages,
          lessonContent,
          subject,
          level,
          background,
          learningNotes,
          age: userAge,
        }),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Tutor failed to respond');
      }

      const reader  = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer    = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop(); // keep incomplete line

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          const payload = line.slice(6).trim();
          if (payload === '[DONE]') break;
          try {
            const { delta, error: streamErr } = JSON.parse(payload);
            if (streamErr) throw new Error(streamErr);
            if (delta) {
              setMessages(prev => {
                const copy = [...prev];
                copy[copy.length - 1] = {
                  ...copy[copy.length - 1],
                  content: copy[copy.length - 1].content + delta,
                };
                return copy;
              });
            }
          } catch {}
        }
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        setError(err.message || 'Something went wrong. Please try again.');
        // Remove the empty placeholder
        setMessages(prev => prev.slice(0, -1));
      }
    } finally {
      setStreaming(false);
      abortRef.current = null;
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [input, messages, streaming, lessonContent, subject, level, background, learningNotes]);

  const handleKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // Render markdown-lite: bold **text** only
  function renderContent(text) {
    const parts = text.split(/(\*\*[^*]+\*\*)/g);
    return parts.map((p, i) =>
      p.startsWith('**') && p.endsWith('**')
        ? <strong key={i}>{p.slice(2, -2)}</strong>
        : p
    );
  }

  return (
    <>
      <div className="hp-backdrop" onClick={onClose} />
      <aside className="hp-panel tutor-panel">
        <div className="hp-header tutor-header">
          <div className="tutor-header-info">
            <span className="tutor-header-icon">🤖</span>
            <div>
              <h2 className="hp-title">AI Tutor</h2>
              <p className="tutor-header-sub">Asking about: <strong>{subject}</strong></p>
            </div>
          </div>
          <button className="hp-close-btn" onClick={onClose}>✕</button>
        </div>

        {/* Message list */}
        <div className="tutor-messages">
          {messages.map((msg, i) => (
            <div
              key={i}
              className={`tutor-msg tutor-msg--${msg.role}`}
            >
              {msg.role === 'assistant' && (
                <span className="tutor-msg-avatar">🤖</span>
              )}
              <div className="tutor-msg-bubble">
                {renderContent(msg.content)}
                {streaming && i === messages.length - 1 && msg.role === 'assistant' && (
                  <span className="tutor-cursor" />
                )}
              </div>
              {msg.role === 'user' && (
                <span className="tutor-msg-avatar tutor-msg-avatar--user">👤</span>
              )}
            </div>
          ))}

          {error && (
            <div className="tutor-error">⚠️ {error}</div>
          )}
          <div ref={bottomRef} />
        </div>

        {/* Input bar */}
        <div className="tutor-input-bar">
          <textarea
            ref={inputRef}
            className="tutor-input"
            placeholder="Ask anything about this lesson…"
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={handleKey}
            rows={2}
            disabled={streaming}
          />
          <button
            className="tutor-send-btn"
            onClick={sendMessage}
            disabled={!input.trim() || streaming}
            title="Send (Enter)"
          >
            {streaming ? <span className="tutor-send-spinner" /> : '➤'}
          </button>
        </div>
        <p className="tutor-hint">Press Enter to send · Shift+Enter for new line</p>
      </aside>
    </>
  );
}
